import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { poultryApi } from '../api/modules';
import { Drawer, DrawerCloseButton } from '../components/ui/Drawer';
import { StatusPill } from '../components/ui/StatusPill';
import { useToast } from '../components/toast/Toast';

interface Props {
  open: boolean;
  code: string | null;
  onClose: () => void;
}

export function PoultryDrawer({ open, code, onClose }: Props) {
  const { show } = useToast();
  const qc = useQueryClient();
  const [dialog, setDialog] = useState<null | 'revise' | 'reject' | 'approve_amount'>(null);
  const [textInput, setTextInput] = useState('');
  const [amount, setAmount] = useState<string>('');

  const { data, isLoading } = useQuery({
    enabled: !!code,
    queryKey: ['poultry', code],
    queryFn: () => poultryApi.detail(code!),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['poultry', code] });
    void qc.invalidateQueries({ queryKey: ['poultry-list'] });
    void qc.invalidateQueries({ queryKey: ['poultry-summary'] });
    void qc.invalidateQueries({ queryKey: ['actions-due'] });
  };

  const advance = useMutation({
    mutationFn: (body: { notes?: string; approved_amount_inr_lakhs?: number } = {}) =>
      poultryApi.advance(code!, body),
    onSuccess: (d) => {
      show(`Advanced · ${d.from} → ${d.to}`);
      setDialog(null);
      setTextInput('');
      setAmount('');
      invalidate();
    },
    onError: (err) => show(`Failed: ${(err as Error).message}`),
  });

  const revise = useMutation({
    mutationFn: (notes: string) => poultryApi.revise(code!, notes),
    onSuccess: () => { show('Sent back for revision · applicant notified'); setDialog(null); setTextInput(''); invalidate(); },
    onError: (err) => show(`Failed: ${(err as Error).message}`),
  });

  const reject = useMutation({
    mutationFn: (reason: string) => poultryApi.reject(code!, reason),
    onSuccess: () => { show('Application rejected · reasons logged'); setDialog(null); setTextInput(''); invalidate(); onClose(); },
    onError: (err) => show(`Failed: ${(err as Error).message}`),
  });

  const isTerminal = data && ['approved', 'rejected', 'withdrawn'].includes(data.stage);
  const isFinalApproval = data && data.stage === 'final_approval';
  const busy = advance.isPending || revise.isPending || reject.isPending;

  return (
    <Drawer open={open && !!code} onClose={onClose}>
      <div className="bg-navy-800 text-white px-6 py-5 flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest opacity-80">Poultry application · {code}</div>
          <h2 className="serif-display text-2xl font-medium mt-1">{data ? data.applicant : 'Loading…'}</h2>
          {data && <div className="text-sm opacity-90 mt-2">{data.sector} · {data.district}</div>}
        </div>
        <DrawerCloseButton onClick={onClose} />
      </div>

      <div className="px-6 py-5 flex-1 overflow-y-auto space-y-5">
        {isLoading || !data ? (
          <div className="h-32 bg-cream-100 rounded-sm animate-pulse" />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-cream-50 p-3 rounded-sm">
                <div className="text-[10px] uppercase tracking-widest text-navy-800/50">DPR amount</div>
                <div className="font-numeric serif-display text-xl text-navy-800 font-medium mt-1">₹{data.dpr_lakhs.toFixed(1)} L</div>
              </div>
              <div className="bg-cream-50 p-3 rounded-sm">
                <div className="text-[10px] uppercase tracking-widest text-navy-800/50">Current stage</div>
                <div className="mt-1"><StatusPill status={data.stage} /></div>
              </div>
              <div className="bg-cream-50 p-3 rounded-sm">
                <div className="text-[10px] uppercase tracking-widest text-navy-800/50">Entity</div>
                <div className="text-sm font-medium text-navy-800 mt-1 capitalize">{data.entity_type}</div>
              </div>
              <div className="bg-cream-50 p-3 rounded-sm">
                <div className="text-[10px] uppercase tracking-widest text-navy-800/50">Submitted</div>
                <div className="font-numeric text-sm text-navy-800 mt-1">
                  {new Date(data.submitted_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xs uppercase tracking-widest text-navy-800/50 mb-3 font-semibold">Approval pipeline</h3>
              <div className="relative">
                <div className="absolute left-3 top-3 bottom-3 w-0.5 bg-cream-200" />
                {[
                  { key: 'submitted_at',      label: 'DPR submitted' },
                  { key: 'cvo_reviewed_at',   label: 'CVO review' },
                  { key: 'pre_appraisal_at',  label: 'Pre-Appraisal Committee' },
                  { key: 'appraisal_at',      label: 'Directorate Appraisal' },
                  { key: 'final_approval_at', label: 'Final approval (Pr. Secretary)' },
                ].map((s, i) => {
                  const ts = data.timeline[s.key];
                  const reached = !!ts;
                  return (
                    <div key={s.key} className="relative pl-10 pb-4">
                      <div className={`absolute left-0 top-0 w-7 h-7 rounded-full ${reached ? 'bg-saffron-500 text-white' : 'bg-cream-100 text-navy-800/40 border border-cream-300'} flex items-center justify-center text-xs font-medium font-numeric`}>
                        {i + 1}
                      </div>
                      <div className={`text-sm font-medium ${reached ? 'text-navy-800' : 'text-navy-800/40'}`}>{s.label}</div>
                      <div className="text-[11px] text-navy-800/60 font-numeric mt-0.5">
                        {reached ? new Date(ts!).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Pending'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <h3 className="text-xs uppercase tracking-widest text-navy-800/50 mb-2 font-semibold">DPR documents</h3>
              <div className="space-y-2">
                {['Project DPR (PDF)', 'Land ownership', 'PAN / GSTIN', 'Bank statement', 'Site photographs', 'Technical compliance'].map((d) => (
                  <div key={d} className="flex items-center justify-between text-xs p-2 border border-cream-200 rounded-sm">
                    <div className="flex items-center gap-2">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0A2540" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" />
                      </svg>
                      <span className="text-navy-800">{d}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => show(`Opening ${d}`)}
                      className="text-saffron-600 hover:text-saffron-700"
                    >
                      View
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {data.notes && (
              <div>
                <h3 className="text-xs uppercase tracking-widest text-navy-800/50 mb-2 font-semibold">Committee notes</h3>
                <p className="text-sm text-navy-800 bg-cream-50 p-3 rounded-sm">{data.notes}</p>
              </div>
            )}
          </>
        )}
      </div>

      {dialog && (
        <div className="border-t border-cream-200 px-6 py-4 bg-amber-50">
          <div className="text-xs uppercase tracking-widest text-navy-800/60 mb-2 font-semibold">
            {dialog === 'reject' ? 'Reason for rejection' : dialog === 'revise' ? 'Revision notes' : 'Approved subsidy amount (₹ lakhs)'}
          </div>
          {dialog === 'approve_amount' ? (
            <input
              type="number"
              min="0" max="99999" step="0.5"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 18.5"
              className="w-full text-sm px-3 py-2 border border-cream-300 rounded-sm bg-white focus:outline-none focus:border-saffron-500 font-numeric"
            />
          ) : (
            <textarea
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              rows={3}
              placeholder={dialog === 'reject' ? 'Required · min 5 chars · will be visible to the applicant' : 'Notes for the applicant…'}
              className="w-full text-sm px-3 py-2 border border-cream-300 rounded-sm bg-white focus:outline-none focus:border-saffron-500"
            />
          )}
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              disabled={busy || (dialog !== 'approve_amount' && textInput.length < 5)}
              onClick={() => {
                if (dialog === 'reject') reject.mutate(textInput);
                else if (dialog === 'revise') revise.mutate(textInput);
                else advance.mutate({ approved_amount_inr_lakhs: parseFloat(amount) || undefined });
              }}
              className={`flex-1 text-white text-sm py-2 rounded-sm disabled:opacity-50 ${
                dialog === 'reject' ? 'bg-red-600 hover:bg-red-700' : 'bg-saffron-500 hover:bg-saffron-600'
              }`}
            >
              {busy ? 'Working…' : 'Confirm'}
            </button>
            <button
              type="button"
              onClick={() => { setDialog(null); setTextInput(''); setAmount(''); }}
              className="px-4 py-2 border border-cream-300 text-navy-800/80 hover:bg-cream-100 text-sm rounded-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="border-t border-cream-200 px-6 py-4 flex items-center gap-2 bg-cream-50">
        {isTerminal ? (
          <button
            type="button"
            onClick={onClose}
            className="flex-1 bg-navy-800 hover:bg-navy-700 text-white text-sm py-2.5 rounded-sm"
          >
            Close — {data!.stage}
          </button>
        ) : (
          <>
            <button
              type="button"
              disabled={busy || !data}
              onClick={() => {
                if (isFinalApproval) {
                  setDialog('approve_amount');
                } else {
                  advance.mutate({});
                }
              }}
              className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm py-2.5 rounded-sm"
            >
              {busy ? 'Working…' : isFinalApproval ? 'Final approval →' : 'Advance to next stage →'}
            </button>
            <button
              type="button"
              disabled={busy || !data}
              onClick={() => { setDialog('revise'); setTextInput(''); }}
              className="px-4 py-2.5 border border-amber-500 text-amber-700 hover:bg-amber-50 disabled:opacity-50 text-sm rounded-sm"
            >
              Request revision
            </button>
            <button
              type="button"
              disabled={busy || !data}
              onClick={() => { setDialog('reject'); setTextInput(''); }}
              className="px-4 py-2.5 border border-red-500 text-red-600 hover:bg-red-50 disabled:opacity-50 text-sm rounded-sm"
            >
              Reject
            </button>
          </>
        )}
      </div>
    </Drawer>
  );
}
